/**
 * Floating Telegram support button.
 *
 * Behaviour:
 * - Two visual states: "expanded" (full circular Telegram button with a ×
 *   close control) and "minimized" (a small tab docked to the same corner
 *   that visitors can click to bring the full button back).
 * - Clicking × collapses the widget to the minimized tab — it is never fully
 *   removed from the screen, just tucked out of the way.
 * - Clicking the minimized tab restores the expanded button.
 * - The chosen state is remembered per visitor via localStorage, so it
 *   persists across pages/visits until they clear their browser storage.
 *
 * This is a plain, dependency-free custom element so it works regardless of
 * theme JS architecture changes, and can be safely reused in other themes.
 */

const STORAGE_KEY = 'telegram-support-button-state';
const STATE_EXPANDED = 'expanded';
const STATE_MINIMIZED = 'minimized';

class TelegramSupportButton extends HTMLElement {
  connectedCallback() {
    this.#applyState(this.#getStoredState());

    const closeButton = this.querySelector('.telegram-support-button__close');
    closeButton?.addEventListener('click', (event) => {
      // Prevent the click from bubbling to the wrapping link.
      event.preventDefault();
      event.stopPropagation();
      this.#setState(STATE_MINIMIZED);
    });

    const minimizedTab = this.querySelector('.telegram-support-button__tab');
    minimizedTab?.addEventListener('click', () => {
      this.#setState(STATE_EXPANDED);
    });
  }

  #getStoredState() {
    try {
      const stored = window.localStorage.getItem(STORAGE_KEY);
      return stored === STATE_MINIMIZED ? STATE_MINIMIZED : STATE_EXPANDED;
    } catch (error) {
      // localStorage can throw in private-browsing modes on some browsers.
      // Fail open: default to expanded rather than breaking the page.
      return STATE_EXPANDED;
    }
  }

  #setState(state) {
    this.#applyState(state);

    try {
      window.localStorage.setItem(STORAGE_KEY, state);
    } catch (error) {
      // Ignore storage errors (e.g. private browsing / storage disabled).
      // The chosen state will simply not persist to the next page view.
    }
  }

  #applyState(state) {
    this.dataset.state = state;
    // No longer needed once we've decided which state to render; keeping the
    // widget hidden any longer would delay both the button and its tab.
    this.removeAttribute('hidden');
  }
}

if (!customElements.get('telegram-support-button')) {
  customElements.define('telegram-support-button', TelegramSupportButton);
}
